import React, {useEffect, useRef, useState} from 'react';
import styles from './player.module.scss';
import ProgressBar from "@/components/global/posts/player/progress-bar";
import {PLAYERSTATUS} from "@/constants";
import VolumeControl from "@/components/global/posts/player/volume-control";
import PlayButton from "@/components/global/posts/player/play-button";

type Props = {
  key?: number,
  file: IFileItem
};

const PostPlayer: React.FC<Props> = ({ file }) => {
    const [currentTime, setCurrentTime] = useState<string>("00:00");
    const [totalTime, setTotalTime] = useState<string>("00:00");
    const [playerStatus, setPlayerStatus] = useState<string>(PLAYERSTATUS.LOADING);
    const [player, setPlayer] = useState<HTMLAudioElement|undefined>();
    const [dataArray, setDataArray] = useState<Float32Array>();
    const [analyser, setAnalyser] = useState<AnalyserNode>();
    const [audioContext, setAudioContext] = useState<AudioContext>();
    const [bgColor, setBgColor] = useState<string>('var(--bs-primary)');

    useEffect(() => {
        const audio = document.createElement('audio');
        audio.src = file.fileSrc as string;
        audio.load();
        audio.crossOrigin = "anonymous";
        setPlayer(audio);
        setPlayerStatus(PLAYERSTATUS.PAUSED);

        return () => {
            audio.src = '';
            audio.pause();
            audio.remove();
            setPlayer(undefined);
        };
    }, [file]);

    useEffect(() => {
        if(!player)
            return;
        const timeUpdate = (e: any) => {
            const pl = e.target as HTMLAudioElement;
            if(pl.duration) {
                setCurrentTime(new Date(pl.currentTime * 1000).toISOString().substring(14, 19));
                setTotalTime(new Date(pl.duration * 1000).toISOString().substring(14, 19));
                if(pl.duration === pl.currentTime && playerStatus !== PLAYERSTATUS.TEMP_PAUSE) {
                    setPlayerStatus(PLAYERSTATUS.REPEAT);
                }
            }
        }
        player.addEventListener('timeupdate', timeUpdate);
        return () => {
            player.removeEventListener('timeupdate', timeUpdate);
        };
    }, [player, playerStatus]);

    const forward = () => {
        if(player)
            player.currentTime += 10;

    };

    const backward = () => {
        if(player)
            player.currentTime -= 10;
    };

    useEffect(() => {
        const actx = new AudioContext();
        if(actx && player) {
            setAudioContext(actx);
            const resumeAudioContext = () => {
                actx.resume();
            };
            player.addEventListener('play', resumeAudioContext);

            return () => player.removeEventListener('play', resumeAudioContext);
        }
    }, [player]);

    useEffect(() => {
        if(audioContext) {
            const analysr = audioContext.createAnalyser();
            const source = audioContext.createMediaElementSource(player as HTMLMediaElement);
            source.connect(analysr);
            analysr.connect(audioContext.destination);
            const bufferLength = analysr.fftSize;
            setAnalyser(analysr);
            const danceInterval = setInterval(() => {
                const da = new Float32Array(bufferLength);
                analysr.getFloatTimeDomainData(da);
                setDataArray(da);
            }, 60);
            return () => {
                source.disconnect();
                clearInterval(danceInterval);
            };
        }
    }, [audioContext]);

    useEffect(() => {
        if(analyser && dataArray) {
            if(playerStatus === PLAYERSTATUS.PLAYING) {
                let sum = 0;
                dataArray.forEach(d => sum += Math.abs(d));
                setBgColor('hsla(' + sum + ', 100%, 50%)');
            } else {
                setBgColor('var(--bs-primary)');
            }
        }
    }, [dataArray]);

    return (
        <div className={styles.playerContainer}>
            <div className={styles.playerInfo}>
                <i className="fa fa-music-alt" />
                <span>{file.title}</span>
            </div>
            <div className={styles.player}>
                <ProgressBar setPlayerStatus={setPlayerStatus} playerStatus={playerStatus} player={player} currentTime={currentTime} totalTime={totalTime} />
                <VolumeControl player={player} />
            </div>
            <div className={styles.buttonContainer}>
                <div className={styles.forward} onClick={forward}>
                    <i className="fa fa-step-forward" />
                </div>
                <PlayButton bgColor={bgColor} setPlayerStatus={setPlayerStatus} player={player} playerStatus={playerStatus} />
                <div className={styles.backward} onClick={backward}>
                    <i className="fa fa-step-backward" />
                </div>
            </div>
            {file.isDownloadable ? (
                <div className="text-end w-100">
                    <a className="btn btn-success" href={file.fileSrc} download>
                        <i className="fa fa-download" /> دانلود
                    </a>
                </div>
            ) : ''}
        </div>
    );
}

export default PostPlayer;
